import React, { useMemo } from 'react'
import { ThreeEvent } from '@react-three/fiber'
import { Vector3 } from 'three'
import { useBrickStore } from '@store/brickStore'
import { SnappingEngine } from '@systems/snapping/SnappingEngine'
import { isValidPlacement } from '@utils/collision'
import { BRICK_CONSTANTS } from '@utils/brickGeometry'
import { useDragDetection } from '@hooks/useDragDetection'
import { PreviewBrick } from './PreviewBrick'
import { BrickInstances } from './BrickInstances'
import type { Brick } from '@models/brick'

/**
 * BrickPlacer Component
 *
 * Main orchestration component for brick placement interaction.
 * Handles:
 * 1. Receiving hover events from BrickInstances component (R3F pointer events)
 * 2. Ground plane pointer events for initial placement
 * 3. Grid snapping using SnappingEngine
 * 4. Preview brick rendering at snapped position
 * 5. Click handling to place bricks
 * 6. Rendering all placed bricks from store using instanced rendering (Task 4.1.1)
 * 7. Drag detection to prevent placement during camera orbit (Task 1.4.7)
 *
 * R3F Pointer Events Strategy:
 * - BrickInstances components have onPointerMove handlers
 * - They detect top surface hits (normal.y > 0.9) and pass to handleBrickHover
 * - Ground plane catches events when not over bricks
 * - Snap intersection point to grid or studs
 * - Update preview position in store
 * - On click, create and store new brick
 *
 * Drag Detection (Task 1.4.7):
 * - Track mouse position on pointerdown
 * - Calculate distance moved on pointerup
 * - Only place brick if movement < 5px (prevents placement during camera orbit)
 * - Cursor feedback: grabbing during drag, default otherwise
 *
 * Performance (Task 4.1.1 - Geometry Instancing):
 * - Uses BrickInstances component for optimized rendering
 * - Bricks grouped by type+color, rendered as instanced meshes
 * - Reduces draw calls from N bricks to M groups (M << N typically)
 * - No manual raycasting needed - R3F handles it
 * - Event propagation stopped at brick level
 * - Snapping engine instance created once with useMemo
 * - Zustand store prevents unnecessary re-renders
 */
export const BrickPlacer: React.FC = () => {
  const snappingEngine = useMemo(() => new SnappingEngine(), [])

  // Drag detection (Task 1.4.7) - prevents placement during camera orbit
  const { handlePointerDown, handlePointerMove: handleDragMove, isDrag } = useDragDetection(5)

  // Store state and actions
  const bricks = useBrickStore((state) => state.bricks)
  const previewPosition = useBrickStore((state) => state.previewPosition)
  const previewRotation = useBrickStore((state) => state.previewRotation)
  const isPreviewValid = useBrickStore((state) => state.isPreviewValid)
  const isSnappedToStud = useBrickStore((state) => state.isSnappedToStud)
  const selectedColor = useBrickStore((state) => state.selectedColor)
  const selectedBrickType = useBrickStore((state) => state.selectedBrickType)
  const addBrick = useBrickStore((state) => state.addBrick)
  const setPreviewPosition = useBrickStore((state) => state.setPreviewPosition)
  const setPreviewValid = useBrickStore((state) => state.setPreviewValid)
  const setSnappedToStud = useBrickStore((state) => state.setSnappedToStud)

  /**
   * Handle hover over placed brick surface (called by PlacedBrick components)
   * Uses R3F pointer events - PlacedBrick detects top surface and passes info here
   *
   * Strategy:
   * 1. PlacedBrick detects top surface hover and passes brick data
   * 2. If hovering over top surface, stack directly on that brick
   * 3. Snap X/Z to grid based on hover point (accounting for brick type!)
   * 4. Set Y position to one brick height above the hovered brick
   */
  const handleBrickHover = (point: Vector3, isTopSurface: boolean, hoveredBrick?: Brick) => {
    // Only process top surface hovers (for stacking)
    if (!isTopSurface || !hoveredBrick) {
      return
    }

    // Stack directly on top of the hovered brick
    // Y position = hovered brick Y + one brick height
    const stackedY = hoveredBrick.position.y + BRICK_CONSTANTS.BRICK_HEIGHT

    // Snap X and Z to grid based on hover point and brick type
    // Use snapToGrid which accounts for brick dimensions
    // Task 3.2.8: Use preview rotation for dimension-aware snapping
    const gridSnapped = snappingEngine.snapToGrid(
      new Vector3(point.x, stackedY, point.z),
      selectedBrickType,
      previewRotation // Use preview rotation for proper snapping
    )

    const snappedPosition = new Vector3(gridSnapped.x, stackedY, gridSnapped.z)

    // Check if this position is valid for placement
    // For stud snapping, we allow vertical overlap since that's intentional stacking
    const isValid = isValidPlacement(
      snappedPosition,
      selectedBrickType,
      previewRotation,
      bricks,
      true
    ) // true = allow stud snap

    // Update preview state
    setPreviewPosition(snappedPosition)
    setPreviewValid(isValid)
    setSnappedToStud(true) // Always true when hovering over brick
  }

  /**
   * Handle pointer move over ground plane
   * Used for initial brick placement when not over existing bricks
   */
  const handleGroundMove = (event: ThreeEvent<PointerEvent>) => {
    // Use the ground plane intersection point from R3F event
    const intersectionPoint = event.point

    // Use SnappingEngine to find grid snap point
    // Task 3.2.8: Use preview rotation for dimension-aware snapping
    const brickArray = Array.from(bricks.values())
    const snapResult = snappingEngine.findSnapPoint(
      intersectionPoint,
      brickArray,
      selectedBrickType,
      previewRotation // Use preview rotation for proper snapping
    )

    const snappedPosition = snapResult.snapPosition
    const isStudSnap = snapResult.snapType === 'stud'

    // Check if this position is valid for placement (no collision)
    const isValid = isValidPlacement(
      snappedPosition,
      selectedBrickType,
      previewRotation,
      bricks,
      false
    )

    // Update preview state
    setPreviewPosition(snappedPosition)
    setPreviewValid(isValid)
    setSnappedToStud(isStudSnap)
  }

  /**
   * Handle pointer leave - hide preview brick
   */
  const handlePointerLeave = () => {
    setPreviewPosition(null)
    setSnappedToStud(false)
  }

  /**
   * Handle click to place brick
   * Creates new brick at snapped position and adds to store
   * Task 1.4.7: Prevents placement if user was dragging (camera orbit)
   * Task 3.2.8: Uses previewRotation for placed brick rotation
   */
  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    // Check if this was a drag (camera orbit) - if so, don't place brick
    if (isDrag(event)) {
      return
    }

    // Prevent placing if no preview position
    if (!previewPosition) return

    // Prevent placing if position is invalid (collision detected)
    if (!isPreviewValid) {
      return
    }

    // Create new brick with unique ID
    // Task 3.2.8: Use previewRotation for placed brick rotation
    const newBrick: Brick = {
      id: `brick-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      type: selectedBrickType,
      position: previewPosition.clone(),
      rotation: previewRotation, // Use preview rotation (Task 3.2.8)
      color: { ...selectedColor },
      connections: [],
    }

    // Add to store
    addBrick(newBrick)

    // After placing, re-check validity at same position
    // (Position is now invalid because we just placed a brick there)
    const stillValid = isValidPlacement(
      previewPosition,
      selectedBrickType,
      previewRotation,
      bricks,
      false
    )
    setPreviewValid(stillValid)

    // Keep preview visible at same position for visual feedback
    // User will see red preview and cannot place another brick here
  }

  /**
   * Combined handler for ground pointer move
   * Handles both preview updates and drag detection (Task 1.4.7)
   */
  const handleGroundPointerMove = (event: ThreeEvent<PointerEvent>) => {
    handleGroundMove(event)
    handleDragMove(event)
  }

  return (
    <>
      {/* Invisible interaction plane for ground placement */}
      {/* Large plane positioned at ground level to catch mouse events when not over bricks */}
      {/* Task 1.4.7: Added drag detection to prevent placement during camera orbit */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
        onPointerDown={handlePointerDown}
        onPointerMove={handleGroundPointerMove}
        onPointerLeave={handlePointerLeave}
        onClick={handleClick}
        visible={false} // Invisible but still receives events
      >
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* Preview brick - shown when hovering over valid placement area */}
      {previewPosition && (
        <PreviewBrick
          position={previewPosition}
          color={selectedColor}
          rotation={previewRotation}
          isValid={isPreviewValid}
          isSnappedToStud={isSnappedToStud}
          type={selectedBrickType}
        />
      )}

      {/* All placed bricks from store - using instanced rendering (Task 4.1.1) */}
      {/* BrickInstances groups bricks by type+color and uses THREE.js instancing */}
      {/* This reduces draw calls from N to M (where M = unique type+color combinations) */}
      <BrickInstances
        bricks={bricks}
        onHover={handleBrickHover}
        onPlaceBrick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerMoveForDrag={handleDragMove}
        isPreviewShowing={!!previewPosition}
      />
    </>
  )
}
